/**
 * CustomerDashboard - ELEGANT WITH ILLUSTRATIONS
 * 
 * Clean white cards with subtle color tints and decorative illustrations
 * Similar style to VendorDashboard
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/api';
import {
  QrCodeIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
  UserCircleIcon,
  StarIcon,
  ArrowRightIcon,
  BellAlertIcon,
  CheckCircleIcon,
  FireIcon,
  BookOpenIcon,
} from '@heroicons/react/24/solid';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  
  .customer-dashboard {
    font-family: var(--font-family-body, 'Inter', -apple-system, BlinkMacSystemFont, sans-serif);
    background: var(--color-background-default, #F8FAFC);
    min-height: 100vh;
  }
  
  /* Main Hero Cards */
  .hero-card {
    background: var(--color-background-surface, white);
    border-radius: 20px;
    border: 1px solid var(--color-border-default, #E2E8F0);
    position: relative;
    overflow: hidden;
    transition: all 0.25s ease;
    cursor: pointer;
  }

  .hero-card:hover {
    border-color: var(--color-border-default, #CBD5E1);
    box-shadow: var(--shadow-card-hover, 0 8px 24px rgba(0, 0, 0, 0.08));
    transform: translateY(-2px);
  }
  
  /* Scan QR Card */
  .scan-card {
    background: linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 50%);
  }
  
  .scan-card .illustration {
    position: absolute;
    right: -20px;
    bottom: -20px;
    width: 160px;
    height: 160px;
    opacity: 0.9;
    transition: transform 0.3s ease;
  }
  
  .scan-card:hover .illustration {
    transform: scale(1.08) rotate(3deg);
  }
  
  .scan-card .accent-icon {
    background: linear-gradient(135deg, #FFEDD5, #FED7AA);
    color: #EA580C;
  }
  
  /* Menu Card - warm appetizing colors */
  .menu-card {
    background: linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 50%);
  }
  
  .menu-card .illustration {
    position: absolute;
    right: -10px;
    bottom: -15px;
    width: 150px;
    height: 150px;
    opacity: 0.95;
    transition: transform 0.3s ease;
  }
  
  .menu-card:hover .illustration {
    transform: scale(1.1) rotate(-3deg);
  }
  
  .menu-card .accent-icon {
    background: linear-gradient(135deg, #FEF08A, #FDE047);
    color: #CA8A04;
  }
  
  /* Orders Card */
  .orders-card {
    background: linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 50%);
  }
  
  .orders-card .illustration {
    position: absolute;
    right: -10px;
    bottom: -10px;
    width: 140px;
    height: 140px;
    opacity: 0.9;
    transition: transform 0.3s ease;
  }
  
  .orders-card:hover .illustration {
    transform: scale(1.08) rotate(2deg);
  }
  
  .orders-card .accent-icon {
    background: linear-gradient(135deg, #DBEAFE, #BFDBFE);
    color: #2563EB;
  }
  
  /* Feature Cards */
  .feature-card {
    background: var(--color-background-surface, white);
    border-radius: 16px;
    border: 1px solid var(--color-border-default, #E2E8F0);
    transition: all 0.25s ease;
    cursor: pointer;
  }

  .feature-card:hover {
    border-color: var(--color-border-default, #CBD5E1);
    box-shadow: var(--shadow-card-hover, 0 8px 20px rgba(0, 0, 0, 0.08));
    transform: translateY(-3px);
  }
  
  .feature-card:active {
    transform: translateY(-1px);
  }
  
  .icon-box {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.25s ease;
  }
  
  .feature-card:hover .icon-box {
    transform: scale(1.1);
  }
  
  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-secondary, #64748B);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  /* Active Order Banner - Enhanced */
  .active-order-banner {
    background: linear-gradient(135deg, var(--color-success, #10B981) 0%, var(--color-brand-primary, #059669) 100%);
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
  }
  
  .active-order-banner::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
    animation: shimmer 2.5s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  
  .active-order-banner:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }
  
  /* Progress Steps */
  .progress-step {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255,255,255,0.3);
    transition: all 0.3s ease;
  }
  
  .progress-step.active {
    background: white;
    box-shadow: 0 0 8px rgba(255,255,255,0.5);
  }
  
  .progress-step.completed {
    background: white;
  }
  
  .progress-line {
    width: 20px;
    height: 2px;
    background: rgba(255,255,255,0.3);
  }
  
  .progress-line.completed {
    background: white;
  }
  
  @keyframes fade-up {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-fade-up {
    animation: fade-up 0.4s ease-out forwards;
  }
  
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
    50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
  }
  
  .pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }
  
  /* Card hover float effect */
  .hero-card:hover {
    transform: translateY(-4px);
  }
  
  /* Icon bounce on load */
  @keyframes icon-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }
  
  .icon-bounce {
    animation: icon-bounce 0.5s ease-out;
  }
  
`;

// QR Code Illustration - BRIGHTER
const QRIllustration = () => (
  <svg viewBox="0 0 160 160" className="w-full h-full" role="img" aria-label="QR code illustration">
    {/* Phone outline */}
    <rect x="40" y="20" width="80" height="120" rx="10" fill="#FB923C" opacity="0.5" />
    <rect x="45" y="30" width="70" height="100" rx="5" fill="white" opacity="0.95" />
    
    {/* QR Code pattern */}
    <rect x="55" y="45" width="50" height="50" fill="#FFEDD5" />
    <rect x="58" y="48" width="12" height="12" fill="#EA580C" opacity="0.9" />
    <rect x="90" y="48" width="12" height="12" fill="#EA580C" opacity="0.9" />
    <rect x="58" y="80" width="12" height="12" fill="#EA580C" opacity="0.9" />
    <rect x="74" y="60" width="8" height="8" fill="#F97316" opacity="0.8" />
    <rect x="74" y="72" width="8" height="8" fill="#F97316" opacity="0.8" />
    <rect x="86" y="66" width="8" height="8" fill="#F97316" opacity="0.8" />
    
    {/* Scan line - animated feel */}
    <path d="M50 70 L110 70" stroke="#F97316" strokeWidth="2" opacity="0.7" strokeDasharray="4 2" />
    
    {/* Camera viewfinder corners */}
    <path d="M50 55 L50 45 L60 45" stroke="#EA580C" strokeWidth="3" fill="none" opacity="0.8" />
    <path d="M100 45 L110 45 L110 55" stroke="#EA580C" strokeWidth="3" fill="none" opacity="0.8" />
    <path d="M110 85 L110 95 L100 95" stroke="#EA580C" strokeWidth="3" fill="none" opacity="0.8" />
    <path d="M60 95 L50 95 L50 85" stroke="#EA580C" strokeWidth="3" fill="none" opacity="0.8" />
  </svg>
);

// Food Menu Illustration - Appetizing plate with food
const MenuIllustration = () => (
  <svg viewBox="0 0 160 160" className="w-full h-full">
    {/* Plate base */}
    <ellipse cx="80" cy="100" rx="55" ry="20" fill="#FEF08A" opacity="0.5" />
    <ellipse cx="80" cy="95" rx="50" ry="18" fill="#FDE047" opacity="0.6" />
    
    {/* Food bowl */}
    <ellipse cx="80" cy="75" rx="40" ry="15" fill="#FBBF24" opacity="0.7" />
    <ellipse cx="80" cy="70" rx="35" ry="12" fill="#FCD34D" opacity="0.9" />
    
    {/* Food items in bowl */}
    <circle cx="70" cy="65" r="10" fill="#F97316" opacity="0.9" />
    <circle cx="90" cy="68" r="8" fill="#EF4444" opacity="0.8" />
    <circle cx="78" cy="72" r="6" fill="#22C55E" opacity="0.9" />
    <ellipse cx="85" cy="60" rx="8" ry="5" fill="#FBBF24" opacity="0.8" />
    
    {/* Steam lines - appetizing */}
    <path d="M65 45 Q60 35 70 30" stroke="#F59E0B" strokeWidth="2" fill="none" opacity="0.5" />
    <path d="M80 42 Q75 30 85 25" stroke="#FBBF24" strokeWidth="2.5" fill="none" opacity="0.6" />
    <path d="M95 45 Q90 35 100 30" stroke="#F59E0B" strokeWidth="2" fill="none" opacity="0.5" />
    
    {/* Utensils */}
    <rect x="35" y="50" width="4" height="45" rx="2" fill="#CA8A04" opacity="0.7" transform="rotate(-15 37 72)" />
    <rect x="120" y="50" width="4" height="45" rx="2" fill="#CA8A04" opacity="0.7" transform="rotate(15 122 72)" />
    
    {/* Menu book hint */}
    <rect x="105" cy="35" width="25" height="30" rx="3" fill="#FEF3C7" opacity="0.9" transform="rotate(10 117 50)" />
    <rect x="108" y="42" width="15" height="2" rx="1" fill="#D97706" opacity="0.6" transform="rotate(10 115 43)" />
    <rect x="108" y="48" width="12" height="2" rx="1" fill="#D97706" opacity="0.4" transform="rotate(10 114 49)" />
  </svg>
);

// Receipt/Orders Illustration - BRIGHTER
const ReceiptIllustration = () => (
  <svg viewBox="0 0 160 160" className="w-full h-full">
    {/* Receipt paper */}
    <path d="M45 25 L45 125 L50 120 L55 125 L60 120 L65 125 L70 120 L75 125 L80 120 L85 125 L90 120 L95 125 L100 120 L105 125 L110 120 L115 125 L115 25 Z" fill="#67E8F9" opacity="0.6" />
    
    {/* Receipt lines */}
    <rect x="55" y="40" width="50" height="4" rx="2" fill="#0891B2" opacity="0.7" />
    <rect x="55" y="52" width="40" height="3" rx="1.5" fill="#06B6D4" opacity="0.5" />
    <rect x="55" y="62" width="45" height="3" rx="1.5" fill="#06B6D4" opacity="0.5" />
    <rect x="55" y="72" width="35" height="3" rx="1.5" fill="#06B6D4" opacity="0.5" />
    
    {/* Total line */}
    <rect x="55" y="88" width="50" height="1" fill="#0891B2" opacity="0.6" />
    <rect x="55" y="95" width="30" height="4" rx="2" fill="#0891B2" opacity="0.8" />
    <rect x="90" y="95" width="15" height="4" rx="2" fill="#0891B2" opacity="0.8" />
    
    {/* Star rating - golden yellow */}
    <path d="M65 108 L67 113 L72 113 L68 116 L70 121 L65 118 L60 121 L62 116 L58 113 L63 113 Z" fill="#F59E0B" />
    <path d="M80 108 L82 113 L87 113 L83 116 L85 121 L80 118 L75 121 L77 116 L73 113 L78 113 Z" fill="#F59E0B" />
    <path d="M95 108 L97 113 L102 113 L98 116 L100 121 L95 118 L90 121 L92 116 L88 113 L93 113 Z" fill="#F59E0B" />
  </svg>
);

const CustomerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  
  const [stats, setStats] = useState({
    activeOrders: 0,
    totalOrders: 0,
    rewardsCount: 0,
    latestOrderStatus: '',
    latestOrderId: null as number | null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Fetch orders
        const ordersResponse = await api.get('/orders');
        const orders = ordersResponse.data || [];
        
        const activeOrders = orders.filter((o: any) => 
          ['PENDING', 'ACCEPTED', 'PREPARING', 'READY'].includes(o.status)
        );
        
        const latestActive = activeOrders[0];
        
        // Fetch rewards count
        let rewardsCount = 0;
        try {
          const rewardsResponse = await api.get('/api/loyalty/customer/rewards');
          rewardsCount = (rewardsResponse.data || []).filter((r: any) => r.rewardAvailable).length;
        } catch {}

        setStats({
          activeOrders: activeOrders.length,
          totalOrders: orders.length,
          rewardsCount,
          latestOrderStatus: latestActive?.status || '',
          latestOrderId: latestActive?.id || null,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Waiting for confirmation';
      case 'ACCEPTED': return 'Order confirmed!';
      case 'PREPARING': return 'Being prepared...';
      case 'READY': return 'Ready for pickup!';
      default: return '';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <ClockIcon className="w-5 h-5" />;
      case 'ACCEPTED': return <CheckCircleIcon className="w-5 h-5" />;
      case 'PREPARING': return <FireIcon className="w-5 h-5" />;
      case 'READY': return <BellAlertIcon className="w-5 h-5" />;
      default: return <ClockIcon className="w-5 h-5" />;
    }
  };

  return (
    <>
      <style>{styles}</style>
      
      <div className="customer-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24">
          
          {/* Header */}
          <header className="mb-3 sm:mb-4 lg:mb-6 animate-fade-up">
            <p className="text-slate-500 text-sm mb-1">{getGreeting()} 👋</p>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>
              {user?.username || 'Guest'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">What would you like to eat today?</p>
          </header>

          {/* Active Order Banner - Enhanced with Progress */}
          {stats.activeOrders > 0 && stats.latestOrderId && (
            <div 
              onClick={() => navigate(`/orders/${stats.latestOrderId}`)}
              className="active-order-banner p-4 mb-6 animate-fade-up pulse-glow"
              style={{ animationDelay: '0.05s' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-white">
                    {getStatusIcon(stats.latestOrderStatus)}
                  </div>
                  <div>
                    <p className="text-white font-semibold">Order in progress</p>
                    <p className="text-white/80 text-sm">{getStatusText(stats.latestOrderStatus)}</p>
                  </div>
                </div>
                <ArrowRightIcon className="w-5 h-5 text-white" />
              </div>
              
              {/* Progress Steps */}
              <div className="flex items-center justify-center gap-1 pt-2">
                <div className={`progress-step ${['PENDING', 'ACCEPTED', 'PREPARING', 'READY'].includes(stats.latestOrderStatus) ? 'completed' : ''}`} />
                <div className={`progress-line ${['ACCEPTED', 'PREPARING', 'READY'].includes(stats.latestOrderStatus) ? 'completed' : ''}`} />
                <div className={`progress-step ${['ACCEPTED', 'PREPARING', 'READY'].includes(stats.latestOrderStatus) ? 'completed' : ''}`} />
                <div className={`progress-line ${['PREPARING', 'READY'].includes(stats.latestOrderStatus) ? 'completed' : ''}`} />
                <div className={`progress-step ${['PREPARING', 'READY'].includes(stats.latestOrderStatus) ? 'completed' : ''} ${stats.latestOrderStatus === 'PREPARING' ? 'active' : ''}`} />
                <div className={`progress-line ${stats.latestOrderStatus === 'READY' ? 'completed' : ''}`} />
                <div className={`progress-step ${stats.latestOrderStatus === 'READY' ? 'completed active' : ''}`} />
              </div>
              <div className="flex justify-between text-white/60 text-xs mt-2 px-2">
                <span>Pending</span>
                <span>Confirmed</span>
                <span>Preparing</span>
                <span>Ready</span>
              </div>
            </div>
          )}

          {/* Main Hero Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-4 sm:mb-6">
            
            {/* Scan QR Card */}
            <div 
              onClick={() => navigate('/scan')}
              className="hero-card scan-card p-3 sm:p-4 lg:p-6 animate-fade-up"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="relative z-10">
                <div className="accent-icon w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4">
                  <QrCodeIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Scan QR</h2>
                <p className="text-slate-500 text-sm mb-4">Scan to view menu & order</p>
                
                <div className="flex items-center gap-2 text-orange-600 font-medium text-sm">
                  Start ordering
                  <ArrowRightIcon className="w-4 h-4" />
                </div>
              </div>
              
              <div className="illustration">
                <QRIllustration />
              </div>
            </div>

            {/* Menu Card */}
            <div 
              onClick={() => navigate('/qr')}
              className="hero-card menu-card p-3 sm:p-4 lg:p-6 animate-fade-up"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="relative z-10">
                <div className="accent-icon w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4">
                  <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Menu</h2>
                <p className="text-slate-500 text-sm mb-4">Browse dishes & prices</p>
                
                <div className="flex items-center gap-2 text-yellow-600 font-medium text-sm">
                  View menu
                  <ArrowRightIcon className="w-4 h-4" />
                </div>
              </div>
              
              <div className="illustration">
                <MenuIllustration />
              </div>
            </div>

            {/* My Orders Card */}
            <div 
              onClick={() => navigate('/orders')}
              className="hero-card orders-card p-3 sm:p-4 lg:p-6 animate-fade-up"
              style={{ animationDelay: '0.2s' }}
            >
              <div className="relative z-10">
                <div className="accent-icon w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-4">
                  <ClipboardDocumentListIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>My Orders</h2>
                <p className="text-slate-500 text-sm mb-4">Order history & receipts</p>
                
                <div className="text-cyan-600 font-semibold text-sm">
                  {stats.totalOrders} orders
                </div>
              </div>
              
              <div className="illustration">
                <ReceiptIllustration />
              </div>
            </div>
          </div>

          {/* More Options */}
          <div className="animate-fade-up" style={{ animationDelay: '0.25s' }}>
            <p className="section-title mb-4">More</p>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              
              {/* Rewards */}
              <div 
                onClick={() => navigate('/rewards')}
                className="feature-card p-4 sm:p-5"
              >
                <div className="icon-box w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)' }}>
                  <GiftIcon className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-slate-500 text-xs mb-0.5">Loyalty</p>
                <h3 className="text-base sm:text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Rewards</h3>
                {stats.rewardsCount > 0 ? (
                  <p className="text-amber-600 font-semibold text-sm">{stats.rewardsCount} available</p>
                ) : (
                  <p className="text-slate-400 text-sm">Earn points</p>
                )}
              </div>

              {/* Profile */}
              <div 
                onClick={() => navigate('/profile')}
                className="feature-card p-4 sm:p-5"
              >
                <div className="icon-box w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ background: 'linear-gradient(135deg, #DBEAFE, #BFDBFE)' }}>
                  <UserCircleIcon className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-slate-500 text-xs mb-0.5">Account</p>
                <h3 className="text-base sm:text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Profile</h3>
                <p className="text-blue-600 text-sm">View details</p>
              </div>

              {/* Favorites */}
              <div 
                onClick={() => navigate('/orders')}
                className="feature-card p-4 sm:p-5"
              >
                <div className="icon-box w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ background: 'linear-gradient(135deg, #FCE7F3, #FBCFE8)' }}>
                  <StarIcon className="w-5 h-5 text-pink-600" />
                </div>
                <p className="text-slate-500 text-xs mb-0.5">Quick access</p>
                <h3 className="text-base sm:text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Recent</h3>
                <p className="text-pink-600 text-sm">Reorder</p>
              </div>

              {/* Enter QR Code */}
              <div 
                onClick={() => navigate('/qr')}
                className="feature-card p-4 sm:p-5"
              >
                <div className="icon-box w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)' }}>
                  <QrCodeIcon className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-slate-500 text-xs mb-0.5">Manual</p>
                <h3 className="text-base sm:text-lg font-semibold mb-1" style={{ fontFamily: 'var(--font-family-heading)', color: theme.text }}>Enter Code</h3>
                <p className="text-emerald-600 text-sm">Type QR code</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default CustomerDashboard;

